/* eslint-disable object-curly-newline, prefer-arrow-callback, @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import {
  Autocomplete, Checkbox, Chip, CircularProgress, FormControl, InputLabel, TextField,
} from '@mui/material';
import { ListChildComponentProps, FixedSizeList } from 'react-window';
import { CommonElementProps } from '../types';
import ElementUtils from '../utils';

export type MultiAutoCompleteOption<T, V> = {
  label: T;
  subLabel?: T;
  value: V;
};

export interface MultiAutoCompleteProps<T, V> extends CommonElementProps<V[]> {
  options: MultiAutoCompleteOption<T, V>[];
  onChange?: (value: V[]) => void;
  onSelectClose?: () => void;
  loading?: boolean;
  classNames?: {
    root?: string,
    label?: string,
  } | undefined,
  enableVirtualization?: boolean;
}

const LISTBOX_PADDING = 8;

function renderRow(props: ListChildComponentProps) {
  const { data, index, style } = props;
  const option = data[index];

  return (
    <div style={{ ...style, top: (style.top as number) + LISTBOX_PADDING }}>
      {option}
    </div>
  );
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

const ListboxComponent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(
  function ListboxComponent(props, ref) {
    const { children, ...other } = props;
    const itemData: React.ReactElement<unknown>[] = [];
    (children as React.ReactElement<unknown>[]).forEach(
      (
        item: React.ReactElement<unknown> & {
          children?: React.ReactElement<unknown>[];
        },
      ) => {
        itemData.push(item);
        itemData.push(...(item.children || []));
      },
    );
    const itemCount = itemData.length;

    return (
      <div ref={ref}>
        <OuterElementContext.Provider value={other}>
          <FixedSizeList
            itemData={itemData}
            height={300}
            width="100%"
            outerElementType={OuterElementType}
            innerElementType="ul"
            itemSize={46}
            overscanCount={5}
            itemCount={itemCount}
          >
            {renderRow}
          </FixedSizeList>
        </OuterElementContext.Provider>
      </div>
    );
  },
);

const MultiAutoComplete = function MultiSelectChip<T, V>(props: MultiAutoCompleteProps<T, V>) {
  const {
    loading,
    options,
    onChange,
    placeholder,
    error,
    label,
    disabled,
    onSelectClose,
    value: defaultValue,
    classNames,
    enableVirtualization,
  } = props;

  const [selectedValues, setSelectedValues] = useState<MultiAutoCompleteOption<T, V>[]>(
    (defaultValue || []).map((value) => options
      .find((opt) => opt.value === value)).filter(Boolean) as MultiAutoCompleteOption<T, V>[],
  );

  // Sync state if defaultValue changes (optional)
  useEffect(() => {
    const mappedValues = (defaultValue || []).map((value) => options
      .find((opt) => opt.value === value))
      .filter(Boolean) as MultiAutoCompleteOption<T, V>[];
    setSelectedValues(mappedValues);
  }, [defaultValue]);

  return (
    <FormControl
      className={twMerge(
        'flex flex-col justify-start gap-1 min-w-[110px] text-left w-full',
        disabled && 'cursor-not-allowed',
        classNames?.root,
      )}
    >
      {label && (
        <InputLabel
          shrink
          color="secondary"
          error={!!error}
          sx={{
            '&.MuiInputLabel-shrink': {
              transform: 'translate(0, 1.5px) scale(1)',
              fontSize: '1rem',
              lineHeight: '23px',
            },
          }}
          className={twMerge('text-[16px] font-bold static leading-tight tracking-wide text-[#28364C] focus:text-[#28364C]', classNames?.label)}
        >
          {label}
        </InputLabel>
      )}
      <Autocomplete
        multiple
        freeSolo
        options={options as any}
        value={selectedValues}
        ListboxComponent={enableVirtualization ? ListboxComponent : undefined}
        filterOptions={(opts, state) => {
          const matchingFromStart: MultiAutoCompleteOption<T, V>[] = [];
          const matchingAnywhere: MultiAutoCompleteOption<T, V>[] = [];

          options.forEach((opt) => {
            const labelCombo = `${opt.label as string} ${opt?.subLabel as string}`.toLowerCase();
            const inputValue = state.inputValue.toLowerCase();

            if (labelCombo.startsWith(inputValue)) {
              matchingFromStart.push(opt);
            } else if (labelCombo.includes(inputValue)) {
              matchingAnywhere.push(opt);
            }
          });

          return [...matchingFromStart, ...matchingAnywhere];
        }}
        getOptionLabel={(option: any) => {
          if (option.subLabel) {
            return `${option.label} ${option.subLabel}`;
          }
          return option.label;
        }}
        sx={{
          '& fieldset': { border: 'none' },
        }}
        loading={loading}
        disabled={disabled}
        defaultValue={(defaultValue || []).map((value) => options.find((v) => v.value === value))}
        disableCloseOnSelect
        fullWidth
        renderTags={(value: any, getTagProps) => (
          value.map((option: any, index: number) => (
            <Chip variant="outlined" label={option.label} {...getTagProps({ index })} />
          ))
        )}
        onClose={() => onSelectClose && onSelectClose()}
        onChange={(e, v: any) => {
          setSelectedValues(v);
          const values = v.map((val: any) => val.value);
          if (onChange) onChange(values);
        }}
        isOptionEqualToValue={(option: any, newValue: any) => option?.value === newValue?.value}
        renderOption={(optionProps, option, { selected }) => (
          <li {...optionProps} title={option?.label as string}>
            <Checkbox
              checked={selected}
              onChange={(e) => {
                if (!option) return;

                const isChecked = e.target.checked;
                const updatedValue = isChecked
                  ? [...(defaultValue || []), option.value]
                  : (defaultValue || []).filter((val) => val !== option.value);
                if (onChange) onChange(updatedValue);
              }}
              size="small"
              className="p-0 pr-2"
            />
            <div className="truncate text-xs">
              {option && (
              <>
                {option.label}
                {' '}
                {option.subLabel && typeof option.subLabel === 'string' && (
                <div className="text-xs text-gray-500">
                  {option.subLabel}
                </div>
                )}
              </>
              )}
            </div>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            sx={{
              '& fieldset': { border: 'none' },
            }}
            variant="outlined"
            className="text-xs"
            placeholder={placeholder}
            InputProps={{
              ...params.InputProps,
              classes: {
                root: disabled ? 'pl-0 text-[#28364C] rounded-md' : 'bg-white custom-shadow pl-0 rounded-md',
                error: 'outline outline-[1px] outline-error',
              },
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={14} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {error && (
        <div className="absolute -bottom-5 text-error text-xs">
          {ElementUtils.renderError(error)}
        </div>
      )}
    </FormControl>
  );
};

MultiAutoComplete.defaultProps = {
  onChange: () => { },
  onSelectClose: () => { },
  loading: false,
  classNames: undefined,
  enableVirtualization: false,
};

MultiAutoComplete.propTypes = {

};
export default MultiAutoComplete;
